'use client';

import { useEffect, useId } from 'react';
import type { EtilizeScriptProps } from './EtilizeScript';

declare global {
  interface Window {
    _babelPolyfill: boolean;
    _customElementsPatched: boolean;
  }
}

/**
 * Attaches an Etilize script to the DOM after removing any existing scripts with the same src.
 ******
 * Each Etilize script executes on load to attach to specific DOM elements and contains initialization code which is not idempotent.
 * Consequently, as we go through virtual navigations, we are forced to reload the script each time so that it will execute against new DOM elements.
 * Since the initialization code is not idempotent, and also since we are unable to "unload" a script,
 * we must reset some global state to ensure that the script will execute again.
 *  - We patch the browser API for custom elements using `redefine-custom-elements`, which allows the spex script to redefine custom elements.
 *  - We reset the _babelPolyfill global variable to false, which allows the finder script to bootstrap babelPolyfills again.
 ******
 * This component is intended to be used in a dynamic import to avoid loading the script and patching state during SSR.
 * Given the async loading of the script, a useEffect hook was avoided to prevent the same script from being attached to the DOM multiple times,
 * either due to updated deps or as a consequence of react-strict mode triggering the effect twice during development.
 ******
 * Ultimately, this is a hack that depends on the specific implementation details of the Etilize scripts.
 * It would be preferable if Etilize provided a more modern API that could be used in an idiomatic way.
 */
export function EtilizeScriptDynamic({ src, ...data }: EtilizeScriptProps) {
  // generate a unique id for the script for this component _instance_
  const id = `etilize${useId()}script`;

  useEffect(() => {
    // check if the script has already been attached to the DOM
    if (!hasScript(id)) {
      // if not, remove any existing scripts with the same src
      removeScripts(src);
      // reset the window state,
      resetWindow();
      // and attach the script
      attachScript(id, { src, ...data });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally avoid deps to prevent duplicate script attachments
  }, [id]);

  return null;
}

function attachScript(id: string, { src, ...data }: EtilizeScriptProps) {
  const script = document.createElement('script');
  script.id = id;
  script.type = 'text/javascript';
  script.async = true;
  script.src = src;

  Object.entries(data).forEach(([key, value]) => {
    script.setAttribute(key, value);
  });

  document.body.appendChild(script);
}

function hasScript(id: string) {
  return document.getElementById(id) !== null;
}

function removeScripts(src: string) {
  const scripts = document.querySelectorAll(`script[src="${src}"]`);

  scripts.forEach(script => {
    script.parentNode?.removeChild(script);
  });
}

function resetWindow() {
  if (typeof window !== 'undefined') {
    window._babelPolyfill = false;

    if (!window._customElementsPatched) {
      require('redefine-custom-elements');
      window._customElementsPatched = true;
    }
  }
}
